import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  Person as PersonIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const AssignmentResponsesScreen = () => {
  const { state } = useLocation();
  const { assignmentId, schoolId } = state || {};
  const [assignmentData, setAssignmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await axios.get(
          `https://kidsm.vercel.app/api/schools/assignments/id/${assignmentId}/responses`,
          { params: { schoolId } }
        );
        setAssignmentData(response.data);
      } catch (error) {
        console.error("Error fetching responses:", error);
        setError(error.response?.data?.error || "Failed to load responses");
      } finally {
        setLoading(false);
      }
    };

    if (assignmentId && schoolId) {
      fetchResponses();
    } else {
      setError("Missing assignment or school ID");
      setLoading(false);
    }
  }, [assignmentId, schoolId]);

  const calculateScore = (answers) => {
    const gradedAnswers = answers.filter((a) => a.isCorrect !== null);
    if (gradedAnswers.length === 0) return "Not graded";
    const correctCount = gradedAnswers.filter((a) => a.isCorrect).length;
    return `${correctCount}/${gradedAnswers.length}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress color="primary" />
        <Typography sx={{ mt: 2, color: "#6200ee" }}>
          Loading responses...
        </Typography>
      </Box>
    );
  }

  if (error || !assignmentData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <Alert severity="error">
          {error || "Failed to load assignment data"}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", pt: 8 }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Card
            sx={{
              p: 3,
              mb: 4,
              borderRadius: "10px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <CardContent>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
              >
                {assignmentData.assignment.title}
              </Typography>
              <Typography sx={{ color: "#6200ee", mb: 1 }}>
                {assignmentData.assignment.subject}
              </Typography>
              <Typography sx={{ color: "#666", mb: 1 }}>
                Due:{" "}
                {new Date(
                  assignmentData.assignment.dueDate
                ).toLocaleDateString()}
              </Typography>
              <Typography sx={{ fontWeight: "bold", color: "#333" }}>
                {assignmentData.responses.length}{" "}
                {assignmentData.responses.length === 1
                  ? "Response"
                  : "Responses"}
              </Typography>
            </CardContent>
          </Card>

          {assignmentData.responses.length === 0 ? (
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent>
                <AssignmentIcon sx={{ fontSize: 50, color: "#ccc" }} />
                <Typography sx={{ fontSize: "18px", color: "#666", mt: 2 }}>
                  No responses yet
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#999" }}>
                  Students haven't submitted this assignment
                </Typography>
              </CardContent>
            </Card>
          ) : (
            assignmentData.responses.map((item) => (
              <Card
                key={item._id}
                sx={{
                  mb: 3,
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PersonIcon sx={{ color: "#6200ee", mr: 1 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {item.learner.fullName}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#666" }}>
                        Admission: {item.learner.admissionNumber}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#6200ee",
                      }}
                    >
                      Score: {calculateScore(item.answers)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#666",
                      fontStyle: "italic",
                      mb: 2,
                    }}
                  >
                    Submitted: {new Date(item.submittedAt).toLocaleString()}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {item.answers.map((answer, idx) => (
                    <Box
                      key={idx}
                      sx={{ mb: 3, pb: 2, borderBottom: "1px solid #eee" }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#333",
                          mb: 1,
                        }}
                      >
                        Q{idx + 1}: {answer.questionText}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "15px", color: "#444", mb: 1 }}
                      >
                        Answer: {answer.answer}
                      </Typography>
                      {answer.questionType === "multiple_choice" && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: answer.isCorrect ? "#4CAF50" : "#f44336",
                            }}
                          >
                            {answer.isCorrect ? "✓ Correct" : "✗ Incorrect"}
                          </Typography>

                          {!answer.isCorrect && answer.correctAnswer && (
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#4CAF50",
                                fontStyle: "italic",
                              }}
                            >
                              Correct answer: {answer.correctAnswer}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default AssignmentResponsesScreen;
